/**
 * The overlay's *default* presentation (ticket 16, FR-61) — the values the
 * overlay-defaults settings screen sets, and the seed a monitor the overlay has
 * never been placed on starts from.
 *
 * These are deliberately kept apart from the overlay's *live* state. FR-24
 * remembers where you dragged the overlay and how transparent you made it, per
 * monitor, across restarts; FR-63 says the on-the-fly hotkey adjustments (FR-27)
 * must not rewrite the stored defaults. So there are two layers:
 *
 * - **live** — per-monitor geometry plus the overlay-wide font size, moved by
 *   dragging and by the font/opacity hotkeys, persisted so a relaunch puts the
 *   prompter back where it was;
 * - **defaults** — the values here. The look half (font size, opacity, the rate)
 *   is set on the settings screen. The shape half is not: on the desktop a
 *   native resize writes it, which is the direction one-window-polish ticket 01
 *   reversed, and the web host — which has no window to drag — still saves its
 *   live prompter into them explicitly.
 *
 * They have to be separate, because FR-18 reloads the last document on launch:
 * if loading a script re-applied the defaults, every launch would throw away the
 * placement FR-24 exists to remember. Nothing re-applies them now — a newly
 * loaded script leaves the window alone entirely — so what they are for is the
 * case nothing has been remembered: a first launch, and a monitor the prompter
 * has never been opened on.
 *
 * Position is stored as fractions of the free space rather than as screen
 * coordinates, because the defaults have to mean something on a monitor the user
 * has not seen yet — a saved `x` of 2100 is meaningless on a 1080p panel.
 */

/** The default overlay presentation. Every field is a number or a boolean, so
 * the whole thing survives a round-trip through JSON without a custom reviver.
 * (This said "every field is a number" until {@link OverlayDefaults.mirror}
 * arrived, and that was never the reason: JSON carries a boolean as faithfully
 * as a number, so a yes-or-no field is stored as one rather than as `0`/`1`.) */
export interface OverlayDefaults {
  width: number;
  height: number;
  /** Window opacity, 0..1. */
  opacity: number;
  /** Prompter font size in px. */
  fontSize: number;
  /** Where the overlay's left edge sits within the free horizontal space:
   * 0 hard left, 0.5 centred, 1 hard right. */
  leftFraction: number;
  /** The same, vertically: 0 at the top of the work area, 1 at the bottom. */
  topFraction: number;
  /**
   * How strongly the prompter's scrim washes out the self-view behind the
   * script, 0..90 (web-ui ticket 08). Web only — the desktop's overlay floats
   * over whatever application is behind it, not over a picture of you, and it
   * stores this without ever reading it.
   *
   * It lives in the shared defaults rather than in a web-only key because this
   * is the blob a settings file exports and a rig in a URL overrides: a value
   * put here is carried by both without either being taught about it. How much
   * of your own frame you want behind your words is a preference — a bright room
   * needs more wash than a dark one — which is the whole reason it is a control
   * and not a constant.
   */
  backdrop: number;
  /**
   * Where the reading line sits down the prompter, as a fraction of its height:
   * 0 at the top, 1 at the bottom (text-align ticket 01). The script flows
   * downward from it, so a low anchor leaves less of the script in view than a
   * high one does.
   *
   * Here for the reason {@link backdrop} is here — this is the blob a settings
   * file exports and a rig in a URL overrides, so a value put here is carried by
   * both hosts without either being taught about it — and, like it, only one
   * host has a control for it. The web's `⋯` menu cycles it; the desktop stores
   * it and reads it, because its prompter is the same component.
   *
   * **Unlike `backdrop`, it is not a preference.** How much wash you want behind
   * your words is taste; where the reader's eyes should land is a fact about
   * where the webcam is bolted, and there is a right answer to it on any given
   * desk. That is why {@link resetLook} leaves it alone.
   */
  anchor: number;
  /**
   * Where a sideways prompter's script sits across the screen, as a fraction of
   * the room either side of it: 0 against the left edge, 1 against the right
   * (text-align ticket 02).
   *
   * {@link anchor}'s twin for a phone on its side, where the front lens is at
   * one end of the screen rather than the top, and a reading line moved up or
   * down brings the eyes no nearer to it. A second field rather than a reading
   * of the first, so that each way up keeps the answer last given in it: turning
   * the phone does not lose where the words were the other way round.
   *
   * Not a preference either, for `anchor`'s reason — it is where the lens is —
   * so {@link resetLook} leaves it alone too. Only the phone has a control for
   * it; the other hosts store it and never read it.
   */
  side: number;
  /**
   * Whether the script is drawn mirrored, left to right, for a hardware
   * teleprompter (prompter-display tickets 02 and 03, the web and the phone). A
   * rig like that lays the screen face-up under a sheet of beam-splitter glass,
   * and the reader sees the screen reflected in it, so text has to be drawn
   * backwards to read forwards.
   *
   * Only the script flips. The status chip, the recording badge and the camera
   * self-view are drawn as they always were, and the recording is untouched by
   * construction on both hosts: it is the camera's own picture, and the
   * prompter is a layer drawn over it (DOM on the web, a view on the phone).
   *
   * Not a preference, for `anchor`'s reason: whether there is glass in front of
   * the lens is a fact about the rig, with a right answer, so {@link resetLook}
   * leaves it alone.
   */
  mirror: boolean;
  /**
   * How far apart the prompter's lines are drawn, as a **factor on each host's
   * own line height** (prompter-display ticket 04): 1 is the line height the
   * host has always drawn at, 1.5 half as much again.
   *
   * A factor and not a line height, because the hosts do not draw at the same
   * one. The web sets its lines at 1.35 and the phone at 1.26, so an absolute
   * built-in would have moved one of them the day this shipped, and 1 moves
   * neither.
   *
   * Unlike `anchor` and `mirror`, this one *is* taste. A large font on a small
   * screen wants the lines closer, so the eye does not drop to the wrong one; a
   * small font on a big monitor wants them further apart, so the next line can
   * be found without searching. Nothing about the rig decides it, so
   * {@link resetLook} puts it back.
   */
  lineSpacing: number;
}

/**
 * The built-in defaults — what a fresh install starts from and what
 * reset-to-defaults (FR-64) restores. Centred horizontally and high up, where a
 * monitor-mounted webcam sits.
 *
 * 760×560 rather than the 640×320 it was through v1. That was a floating strip's
 * size: a frameless click-through band of script with nothing else in it. The
 * prompter is an ordinary window now, with a titlebar above the script and a
 * transport bar under it, and 320px of height with both of those in it is not a
 * window anybody can read from.
 */
export const DEFAULT_OVERLAY: OverlayDefaults = {
  width: 760,
  height: 560,
  opacity: 1,
  fontSize: 30,
  leftFraction: 0.5,
  topFraction: 0.08,
  /* Enough that white words hold over a lit face, and little enough that you can
   * still see whether you are in frame. */
  backdrop: 55,
  /* {@link ANCHOR_STOPS}' middle, which is where the reading line already rode
   * before it could be moved at all — `ANCHOR_FRACTION` in `OverlayApp`. Not
   * 0.5: the script flows *downward* from the anchor, so a block anchored a
   * third of the way down has its optical centre near the middle of the screen,
   * and choosing the arithmetic middle instead would have moved every prompter
   * in existence on the day this shipped. */
  anchor: 0.33,
  /* {@link SIDE_STOPS}' middle, where a sideways column sat before it could be
   * moved. */
  side: 0.5,
  /* Glass is the exception: a prompter read straight off the screen is every
   * prompter there was before this field. */
  mirror: false,
  /* The host's own line height, untouched. */
  lineSpacing: 1,
};

/**
 * Hard limits the shortcut steppers and the settings inputs clamp to.
 *
 * `minOpacity` is deliberately clear of zero so opacity-down can never make the
 * window vanish (FR-49 spirit), and the size floors keep the settings screen
 * from producing a window too small to read or to find.
 *
 * **`minWidth` and `minHeight` are also the window's own minimum size**, set on
 * the `BrowserWindow` from these constants. They have to be the same numbers:
 * `setBounds` with a smaller rect silently gets Electron's minimum while the
 * controller records what it *asked for*, and the two then disagree forever —
 * every relaunch restoring a size the window will not take. 480×360 rather than
 * 240×120 for the same reason the default grew: a framed window with a transport
 * bar in it has furniture the strip did not.
 */
export const OVERLAY_LIMITS = {
  minFont: 16,
  maxFont: 96,
  fontStep: 2,
  minOpacity: 0.2,
  maxOpacity: 1,
  opacityStep: 0.1,
  minWidth: 480,
  maxWidth: 7680,
  minHeight: 360,
  maxHeight: 4320,
  /* The scrim's floor is genuinely nothing — a dark room with a dim frame wants
   * no wash at all, and a slider that could not reach zero would be describing a
   * range this product does not have.
   *
   * **The ceiling is 90 rather than 100**, and that is the same argument as
   * `minOpacity`: a wash that can reach opaque is a self-view somebody can
   * switch off by accident and then go hunting for. The control that means "off"
   * is the camera toggle, which says so. */
  minBackdrop: 0,
  maxBackdrop: 90,
  /* Line spacing's range (prompter-display ticket 04), as a factor on the
   * host's own line height: a tenth closer than it at the bottom, twice as far
   * apart at the top. Whether the bottom earns its place and the top is far
   * enough is a question for somebody reading aloud at both ends, and the
   * ticket leaves it to them. */
  minLineSpacing: 0.9,
  maxLineSpacing: 2,
} as const;

/**
 * What the settings screens' sliders offer, as against what
 * {@link OVERLAY_LIMITS} allows.
 *
 * There were width and height tracks here, with maxima of their own: the hard
 * limits have to reach an 8K monitor, and a slider spanning 480 to 7680px would
 * have moved in useless jumps for the sizes anyone actually uses. Neither host
 * offers them any more. The desktop's window is sized by its own edges and writes
 * the result back into the defaults itself, so a slider would be a second, worse
 * way to set what the drag already set; a browser tab never had a window to size.
 *
 * That leaves one entry, and it is deliberately *equal* to a hard limit rather
 * than narrower than one: the slider has to move in the same notch the font
 * hotkeys do, or dragging and `Ctrl+Alt+=` would disagree about what a step is.
 * It reads from {@link OVERLAY_LIMITS} so the two cannot drift, and it is here
 * rather than at the two screens because "what the slider's `step` is" is a
 * shared decision and both hosts have to make it the same way.
 */
export const OVERLAY_SLIDERS = {
  /** Same step as the font hotkeys, so dragging and `Ctrl+Alt+=` agree. */
  fontStep: OVERLAY_LIMITS.fontStep,
  /** The backdrop track (web-ui ticket 08). Five points, because the wash is a
   * judgement made by eye against a live frame and a percentage-at-a-time slider
   * is eighteen drags of a control nobody can see a single step of. It divides
   * the 0..90 range exactly, so both ends are positions the slider lands on. */
  backdropStep: 5,
  /** The line-spacing track (prompter-display ticket 04): a tenth at a time,
   * the one decimal it is stored to, so every notch is a value the store keeps
   * as it is. Eleven steps divide the 0.9..2.0 range exactly. */
  lineSpacingStep: 0.1,
} as const;

/** The constant-speed rate applied when no explicit WPM has been saved, and what
 * the overlay-defaults screen's reset restores (FR-35/FR-61/FR-64). A relaxed
 * piece-to-camera pace — comfortably below open dictation speed. */
export const DEFAULT_CONSTANT_SPEED_WPM = 130;

/** The range the settings screen offers for the constant-speed rate. The floor
 * is slow enough to be a crawl and the ceiling faster than anyone reads aloud;
 * outside it the mode is not a teleprompter. */
export const CONSTANT_SPEED_WPM_LIMITS = { min: 40, max: 400 } as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Which way a stepping shortcut goes: up, or down. */
export type StepDirection = 1 | -1;

/**
 * Read a direction that arrived from a renderer or a page.
 *
 * Forward is `1` and everything else is back — a bad payload, a missing one, a
 * string. One function rather than the same ternary at each of the four channels
 * that take a direction, so the two hosts cannot end up disagreeing about what a
 * malformed one means. Erring towards back is the cheaper mistake for both
 * things this is used for: it leaves the reader behind where they were rather
 * than ahead of themselves, and the font smaller rather than larger.
 */
export function asStepDirection(value: unknown): StepDirection {
  return value === 1 ? 1 : -1;
}

/**
 * Step the prompt's font size one notch, clamped (FR-27).
 *
 * In the core rather than beside the desktop's window geometry, where it used to
 * be, because it is not geometry: the font is one overlay-wide value that moving
 * the overlay between monitors does not change, and the web has no window at all
 * yet still steps it from a shortcut (web ticket 10). Both hosts step it the
 * same way or `Ctrl+Alt+=` and `=` would disagree about what a notch is.
 */
export function stepFontSize(px: number, dir: StepDirection): number {
  return clamp(
    px + dir * OVERLAY_LIMITS.fontStep,
    OVERLAY_LIMITS.minFont,
    OVERLAY_LIMITS.maxFont,
  );
}

/**
 * Step the line spacing one notch of the web's slider, clamped
 * (prompter-display ticket 05) — the phone's stepper, which has no slider.
 *
 * Rounded to the tenth it is stored at, as {@link sanitiseOverlayDefaults}
 * would round it: `1.1 + 0.1` is `1.2000000000000002` in floating point, and a
 * screen that steps from what it holds would carry the error into the next
 * press and show it until the host's broadcast came back.
 */
export function stepLineSpacing(spacing: number, dir: StepDirection): number {
  return round1(
    clamp(
      spacing + dir * OVERLAY_SLIDERS.lineSpacingStep,
      OVERLAY_LIMITS.minLineSpacing,
      OVERLAY_LIMITS.maxLineSpacing,
    ),
  );
}

/**
 * The three places the reading line is allowed to sit (text-align ticket 01), as
 * fractions of the prompter's height.
 *
 * Three stops and not a slider, because there are only three answers to the
 * question behind it: the webcam is above the screen, in the screen's bezel, or
 * below it. A track with a hundred positions on it would be asking a reader to
 * dial in a number for a fact they can only point at.
 *
 * `0.12` is as high as the words can go before they meet the prompter's own
 * chrome; `0.33` is where the line has ridden since there was a line, which is
 * what makes this ticket move nobody's prompter; `0.75` is low enough for a
 * camera under the monitor and still leaves a line or two of what is coming.
 */
export const ANCHOR_STOPS: readonly number[] = [0.12, 0.33, 0.75];

/**
 * Which stop a stored anchor is nearest, for everything that has to talk about
 * the anchor in the control's terms rather than the field's.
 *
 * The stops are what the *control* offers; the field takes any fraction, because
 * a hand-edited settings file and a rig in a URL both reach it without passing a
 * menu. So there are two questions with one answer — what does the next press
 * move on from, and what is this called on screen — and they must not be
 * answered twice. A menu naming one stop while the press moved on from another
 * is the whole failure this is one function to prevent.
 */
export function nearestAnchorStop(current: number): number {
  if (!Number.isFinite(current)) return DEFAULT_OVERLAY.anchor;
  return ANCHOR_STOPS.reduce((best, stop) =>
    Math.abs(stop - current) < Math.abs(best - current) ? stop : best,
  );
}

/**
 * The next stop round, for the one control that sets the anchor.
 *
 * In the core beside {@link stepFontSize} for the reason that one is here: both
 * hosts have to agree what a step is, or the same press would mean different
 * things in a tab and in the window.
 *
 * **It snaps before it steps** — see {@link nearestAnchorStop}. Cycling from the
 * literal stored value instead would be a press that appeared to do nothing,
 * because the cycle would have started somewhere the reader cannot see.
 */
export function cycleAnchor(current: number): number {
  const from = ANCHOR_STOPS.indexOf(nearestAnchorStop(current));
  return ANCHOR_STOPS[(from + 1) % ANCHOR_STOPS.length]!;
}

/**
 * The three places a sideways prompter's column is allowed to sit (text-align
 * ticket 02): against the left edge, in the middle, against the right.
 *
 * Three for the reason {@link ANCHOR_STOPS} is three — the lens is at one end,
 * at the other, or the phone is not filming the reader at all — and in the same
 * order as a reader names them, so the walk is centre, right, left.
 */
export const SIDE_STOPS: readonly number[] = [0, 0.5, 1];

/** Which of {@link SIDE_STOPS} a stored side is nearest — {@link
 * nearestAnchorStop}'s twin, for the same two questions. */
export function nearestSideStop(current: number): number {
  if (!Number.isFinite(current)) return DEFAULT_OVERLAY.side;
  return SIDE_STOPS.reduce((best, stop) =>
    Math.abs(stop - current) < Math.abs(best - current) ? stop : best,
  );
}

/** The next side round. Snaps before it steps, as {@link cycleAnchor} does. */
export function cycleSide(current: number): number {
  const from = SIDE_STOPS.indexOf(nearestSideStop(current));
  return SIDE_STOPS[(from + 1) % SIDE_STOPS.length]!;
}

/**
 * Narrow a constant-speed rate into the usable range, falling back to the default
 * when it is not a rate at all. The timer divides by this, so a zero or a negative
 * would stall the prompter or run it backwards.
 *
 * The one place the rule lives, for every way a rate can arrive: a settings file,
 * an IPC payload, and the string a number input hands over — where a *cleared*
 * box is an empty string, which `Number` reads as 0 and the clamp would then turn
 * into the slowest rate on the dial. A field with nothing in it means the default,
 * not a crawl.
 */
export function sanitiseWpm(value: unknown): number {
  const wpm =
    typeof value === "string"
      ? value.trim() === ""
        ? Number.NaN
        : Number(value)
      : value;
  if (typeof wpm !== "number" || !Number.isFinite(wpm)) {
    return DEFAULT_CONSTANT_SPEED_WPM;
  }
  return Math.round(
    clamp(wpm, CONSTANT_SPEED_WPM_LIMITS.min, CONSTANT_SPEED_WPM_LIMITS.max),
  );
}

/** Clamp a value that arrived from a settings input or a settings file, falling
 * back to the built-in when it is not a usable number at all. */
function field(
  value: unknown,
  min: number,
  max: number,
  fallback: number,
  round: (n: number) => number,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return round(clamp(value, min, max));
}

/** A yes-or-no that arrived from a settings file or a renderer: `true` or
 * `false` exactly, and the built-in for anything else. {@link field}'s twin for
 * the one kind of value that is not a number. Strict rather than truthy, so a
 * hand-edited `"false"` is not read as yes. */
function flag(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

/** Round to one decimal so a stored opacity never carries float noise into the
 * window (e.g. 0.7000000000000001). */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Round a fraction to two decimals — a percentage is the finest control the
 * settings screen offers, and finer values only accumulate noise. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Narrow anything — a settings file that was hand-edited, a payload from a
 * renderer — into a complete, in-range {@link OverlayDefaults}.
 *
 * Total by construction: every field falls back to its built-in rather than
 * being dropped, so callers never deal with a half-populated defaults object and
 * a corrupt settings file can never place the overlay somewhere unusable.
 */
export function sanitiseOverlayDefaults(value: unknown): OverlayDefaults {
  const raw = (typeof value === "object" && value !== null ? value : {}) as
    Record<string, unknown>;
  const d = DEFAULT_OVERLAY;
  return {
    width: field(raw.width, OVERLAY_LIMITS.minWidth, OVERLAY_LIMITS.maxWidth, d.width, Math.round),
    height: field(raw.height, OVERLAY_LIMITS.minHeight, OVERLAY_LIMITS.maxHeight, d.height, Math.round),
    opacity: field(raw.opacity, OVERLAY_LIMITS.minOpacity, OVERLAY_LIMITS.maxOpacity, d.opacity, round1),
    fontSize: field(raw.fontSize, OVERLAY_LIMITS.minFont, OVERLAY_LIMITS.maxFont, d.fontSize, Math.round),
    leftFraction: field(raw.leftFraction, 0, 1, d.leftFraction, round2),
    topFraction: field(raw.topFraction, 0, 1, d.topFraction, round2),
    backdrop: field(
      raw.backdrop,
      OVERLAY_LIMITS.minBackdrop,
      OVERLAY_LIMITS.maxBackdrop,
      d.backdrop,
      Math.round,
    ),
    /* A fraction of the prompter's height, so it clamps and rounds exactly as
     * `leftFraction` and `topFraction` do — the same two decimals, for the same
     * reason: finer than any control offers, and finer values only accumulate
     * noise across round-trips. */
    anchor: field(raw.anchor, 0, 1, d.anchor, round2),
    side: field(raw.side, 0, 1, d.side, round2),
    mirror: flag(raw.mirror, d.mirror),
    /* One decimal, which is the slider's step, so a range input's float noise
     * (1.2000000000000002) never reaches the blob. */
    lineSpacing: field(
      raw.lineSpacing,
      OVERLAY_LIMITS.minLineSpacing,
      OVERLAY_LIMITS.maxLineSpacing,
      d.lineSpacing,
      round1,
    ),
  };
}

/**
 * The built-in *look* over whatever shape is already stored — what
 * reset-to-defaults (FR-64) leaves behind.
 *
 * Reset restores the font size, the opacity, the backdrop and the line spacing,
 * and nothing else.
 * The width, height and fractions are not the screen's to reset, for the same
 * reason they are not the screen's to set: a native resize is what writes them
 * (one-window-polish ticket 01), and they are the seed for a monitor the
 * prompter has never been opened on. Resetting them would throw away a shape
 * the user set by dragging, and send the next monitor back to 760×560 — which
 * is what dropping the whole record used to do.
 *
 * **`anchor` is left alone too, and for a different reason** (text-align ticket
 * 01). It is not a shape somebody dragged and it *is* set from a control, so by
 * the argument above it would belong in the list — except that what it records
 * is where the webcam is bolted, which is a fact about the reader's desk rather
 * than a value they chose by taste. A reset that moved it would be undoing a
 * correct answer to a physical question, and the reader would then be looking
 * away from their lens with no idea why. The look is what somebody can want back;
 * this is not one of those. `side` is the same fact for a phone on its side, and
 * is left alone with it. So is `mirror` (prompter-display ticket 02): whether
 * there is teleprompter glass in front of the lens is the same kind of fact.
 *
 * Here rather than in the controller because it is the definition of what the
 * look half *is*, and {@link DEFAULT_OVERLAY} is what it is defined against.
 */
export function resetLook(stored: OverlayDefaults): OverlayDefaults {
  return {
    ...stored,
    fontSize: DEFAULT_OVERLAY.fontSize,
    opacity: DEFAULT_OVERLAY.opacity,
    /* A look value like the two above it, and set in the same section of the
     * same screen — so reset is the way back from a wash dialled to 90 over a
     * dark frame, which is exactly the state somebody reaches for reset in. */
    backdrop: DEFAULT_OVERLAY.backdrop,
    /* Taste, like the font size it sits beside on the screen (prompter-display
     * ticket 04): how far apart the lines are is what somebody can want back,
     * not a fact about their desk. */
    lineSpacing: DEFAULT_OVERLAY.lineSpacing,
  };
}

/** What the overlay-defaults screen is a view onto (FR-61): the stored defaults
 * and the constant-speed rate.
 *
 * Deliberately carries no "these are already the built-ins" flag to grey the
 * reset control with. Reset also *applies* the built-ins to the overlay, so it
 * has work to do even when the stored values are unchanged — it is the way back
 * from an overlay dragged somewhere unusable, which is exactly the moment a
 * greyed button would be worst. */
export interface OverlaySettingsState {
  defaults: OverlayDefaults;
  wpm: number;
}
